import { useUser } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import { useEnsuredCurrentUser } from "../lib/use-current-user";
import { AppHeader } from "../components/AppHeader";
import { MaintenanceSchedule } from "../components/MaintenanceSchedule";
import { RequestsSection } from "../components/RequestsSection";
import { MyLeaseSection } from "../components/MyLeaseSection";
import { MyRentSection } from "../components/MyRentSection";
import { MyDocumentsSection } from "../components/MyDocumentsSection";
import { ExpenseRequestsSection } from "../components/ExpenseRequestsSection";
import { FaqSection } from "../components/FaqSection";
import { QandASection } from "../components/QandASection";
import { useEffectiveRole } from "../lib/view-as-context";

type Role = "landlord" | "tenant" | null | undefined;


export default function Dashboard() {
  const property = useProperty();
  const { user } = useUser();
  const me = useEnsuredCurrentUser();
  const realRole = useQuery(
    anyApi.memberships.getMyRoleOnProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as Role;
  const role = useEffectiveRole(realRole);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-10">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            {greetingName(me, user) ? `Hi, ${greetingName(me, user)}.` : "Welcome."}
          </h1>
          <p className="mt-2 text-slate-600">Your unit dashboard.</p>
        </div>

        {me.state === "loading" || me.state === "syncing" ? (
          <SettingUp />
        ) : (
          <>
            <MaintenanceSchedule overrideRole={role ?? undefined} />
            <RequestsSection />
            {role === "tenant" && <MyLeaseSection />}
            {role === "tenant" && <MyRentSection />}
            {role === "tenant" && <MyDocumentsSection />}
            <ExpenseRequestsSection role={role} />
            <FaqSection realRole={realRole} />
            <QandASection />
          </>
        )}

      </main>
    </div>
  );
}

// Prefer the firstName we patched onto the Convex user row (which
// gets it from the landlord's invite when Clerk's sign-up form
// didn't collect it); fall back to whatever Clerk has.
function greetingName(
  me: ReturnType<typeof useEnsuredCurrentUser>,
  clerkUser: ReturnType<typeof useUser>["user"],
): string | null {
  if (me.state === "ready" && me.user.firstName) return me.user.firstName;
  return clerkUser?.firstName ?? null;
}

function SettingUp() {
  return (
    <section className="rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
      <p className="text-sm font-medium text-slate-900">
        Setting up your account...
      </p>
      <p className="mt-1 text-sm text-slate-500">
        One moment while we link your sign-in to your unit.
      </p>
    </section>
  );
}

